import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { shouldExcludeFromAnalytics } from '@/lib/analyticsExclusions';
import { captureAcquisition, ensureIdentifiers } from '@/lib/attributionCapture';

// Use the SAME session ID as useVisitorPresence for unified tracking
const getSessionId = () => {
  // First check if visitor presence already created one
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  // Also store under analytics key for backward compat
  sessionStorage.setItem('analytics_session_id', sessionId);
  return sessionId;
};

// Get persistent visitor ID (shared with useVisitorPresence)
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('lt_visitor_id');
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem('lt_visitor_id', visitorId);
    localStorage.setItem('lt_first_visit', Date.now().toString());
  }
  return visitorId;
};

const getIsReturning = (): boolean => {
  const firstVisit = localStorage.getItem('lt_first_visit');
  const visitCount = parseInt(localStorage.getItem('lt_visit_count') || '0', 10);
  
  if (!sessionStorage.getItem('lt_visit_counted')) {
    localStorage.setItem('lt_visit_count', (visitCount + 1).toString());
    sessionStorage.setItem('lt_visit_counted', 'true');
    return visitCount > 0 && !!firstVisit;
  }
  
  return visitCount > 1 && !!firstVisit;
};

// Detect browser from user agent
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  return 'Other';
};

// Heuristic: URLs only distributed via email/SMS — if entry is here with no
// other attribution signals, it's an email/SMS click (referrer stripped by client).
const isEmailDistributedPath = (pathname: string): boolean => {
  if (/^\/booking\/[^/]+\/(quote-review|payment|confirm|review|signature|insurance|booking-details)/i.test(pathname)) return true;
  if (/^\/sign\/VB\d+/i.test(pathname)) return true;
  if (pathname === '/resume-booking') return true;
  if (pathname === '/customer-payment') return true;
  return false;
};

// Get traffic source from URL params or referrer
const getTrafficSource = (search: string, pathname?: string) => {
  const urlParams = new URLSearchParams(search);
  const utmSource = urlParams.get('utm_source');
  if (urlParams.get('gclid') || urlParams.get('gad_source')) return 'google_ads';
  if (utmSource === 'sms' || urlParams.get('utm_medium') === 'sms') return 'sms';
  if (utmSource === 'email' || utmSource === 'signature') return 'email';
  if (utmSource) return utmSource;
  if (urlParams.get('fbclid')) return 'facebook';
  if (document.referrer.includes('google')) return 'google_organic';
  if (document.referrer.includes('facebook') || document.referrer.includes('instagram')) return 'facebook';
  if (document.referrer.includes('brave')) return 'brave_search';
  if (document.referrer.includes('reddit')) return 'reddit';
  if (document.referrer.includes('lovable.dev') || document.referrer.includes('lovableproject.com')) return 'direct';
  if (document.referrer && !document.referrer.includes(window.location.hostname)) return 'referral';
  // Fallback: if entry page is only reachable via email/SMS, classify as email
  if (pathname && isEmailDistributedPath(pathname)) return 'email';
  return 'direct';
};

// Extract referrer domain for referral sources
const getReferrerDomain = (): string | null => {
  if (!document.referrer) return null;
  try {
    const url = new URL(document.referrer);
    const host = url.hostname.replace(/^www\./, '');
    if (host.includes('lovable.dev') || host.includes('lovableproject.com')) return null;
    return host;
  } catch {
    return null;
  }
};

// Client-side bot detection patterns
const BOT_UA_PATTERNS = [
  /googlebot/i, /bingbot/i, /baiduspider/i, /yandexbot/i, /duckduckbot/i,
  /bytespider/i, /petalbot/i, /sogou/i, /slurp/i,
  /bot\b/i, /crawler/i, /spider/i, /scraper/i, /headless/i,
  /python/i, /curl\//i, /wget\//i, /node-fetch/i, /go-http-client/i, /java\//i, /ruby/i, /perl/i, /libwww/i, /httpie/i, /axios\//i,
  /semrush/i, /ahrefs/i, /dataforseo/i, /screaming frog/i, /majestic/i,
  /facebookexternalhit/i, /twitterbot/i, /linkedinbot/i, /whatsapp/i, /telegrambot/i, /slackbot/i, /discordbot/i, /pinterest/i,
  /gptbot/i, /claudebot/i, /anthropic/i, /ccbot/i, /applebot/i,
  /nmap/i, /nikto/i, /sqlmap/i, /dirbuster/i, /burpsuite/i, /masscan/i, /zgrab/i, /censys/i, /shodan/i,
];

const isBot = (userAgent: string): boolean => {
  if (!userAgent || userAgent.length < 20) return true;
  return BOT_UA_PATTERNS.some(p => p.test(userAgent));
};

// Headless / automation fingerprint — catches scripted hitters that spoof a
// normal UA but leave telltale traces (webdriver flag, no plugins, no
// languages, missing chrome runtime on Chromium, etc.).
const isLikelyHeadless = (): boolean => {
  try {
    const nav: any = navigator;
    if (nav.webdriver === true) return true;
    const ua = navigator.userAgent || '';
    const isChromium = /Chrome|Chromium|Edg/i.test(ua);
    const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    const languages = Array.isArray(nav.languages) ? nav.languages : [];
    const plugins = nav.plugins ? nav.plugins.length : 0;
    // Real Chromium desktop browsers always expose window.chrome and >=1 plugin
    if (isChromium && !isMobile) {
      if (!(window as any).chrome) return true;
      if (plugins === 0 && languages.length === 0) return true;
    }
    // No languages at all is a strong headless signal on any browser
    if (languages.length === 0 && !isMobile) return true;
    // 1×1 or 0×0 viewport = scripted
    if (window.innerWidth < 100 || window.innerHeight < 100) return true;
    return false;
  } catch {
    return false;
  }
};

// Resolves when the visitor shows a human signal (interaction, 2nd pageview,
// or ≥3s dwell). If the tab is closed / navigated away before any signal, the
// promise never resolves — so no traffic_sessions row is written for scripted
// 0-second single-pageview hits.
let humanSignalPromise: Promise<void> | null = null;
const waitForHumanSignal = (): Promise<void> => {
  if (humanSignalPromise) return humanSignalPromise;
  humanSignalPromise = new Promise<void>((resolve) => {
    if (sessionStorage.getItem('lt_human_signal') === '1') {
      resolve();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      sessionStorage.setItem('lt_human_signal', '1');
      window.removeEventListener('pointermove', finish);
      window.removeEventListener('pointerdown', finish);
      window.removeEventListener('touchstart', finish);
      window.removeEventListener('keydown', finish);
      window.removeEventListener('scroll', finish);
      window.removeEventListener('popstate', finish);
      clearTimeout(timer);
      resolve();
    };
    window.addEventListener('pointermove', finish, { passive: true, once: true });
    window.addEventListener('pointerdown', finish, { passive: true, once: true });
    window.addEventListener('touchstart', finish, { passive: true, once: true });
    window.addEventListener('keydown', finish, { once: true });
    window.addEventListener('scroll', finish, { passive: true, once: true });
    window.addEventListener('popstate', finish, { once: true });
    // 3s dwell fallback — real visitors who read without interacting still count
    const timer = setTimeout(finish, 3000);
  });
  return humanSignalPromise;
};

// Format date as YYYY-MM-DD without date-fns
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Extract VB number from current path or session storage
const extractVBNumber = (pathname: string): string | null => {
  const currentVB = sessionStorage.getItem('current_vb_number');
  if (currentVB) return currentVB;

  if (pathname === '/resume-booking') {
    return sessionStorage.getItem('pending_vb_lookup') || null;
  }

  const bookingMatch = pathname.match(/\/booking\/([^/]+)/);
  if (bookingMatch) {
    const bookingId = bookingMatch[1];
    return sessionStorage.getItem(`booking_vb_${bookingId}`) || null;
  }
  
  const signMatch = pathname.match(/\/sign\/(VB\d+)/i);
  if (signMatch) {
    return signMatch[1].toUpperCase();
  }
  
  if (pathname === '/job-inventory') {
    return sessionStorage.getItem('current_job_vb') || null;
  }
  
  return null;
};

// Get stored page history from sessionStorage
const getStoredPageHistory = (): string[] => {
  try {
    // Use visitor_page_history (shared with useVisitorPresence)
    const stored = sessionStorage.getItem('visitor_page_history') || sessionStorage.getItem('analytics_page_history');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Get stored pages viewed count
const getStoredPagesViewed = (): number => {
  const stored = sessionStorage.getItem('analytics_pages_viewed');
  return stored ? parseInt(stored, 10) : 0;
};

// Get workflow step from path
const getWorkflowStep = (pathname: string): string | null => {
  if (pathname === '/' || pathname === '/quote') return 'estimate_form';
  const match = pathname.match(/\/booking\/[^/]+\/([^/]+)/);
  return match ? match[1] : null;
};

export function usePageAnalytics() {
  const location = useLocation();
  const sessionId = useRef(getSessionId());
  const pagesViewed = useRef<number>(getStoredPagesViewed());
  const pageHistory = useRef<string[]>(getStoredPageHistory());
  const pathnameRef = useRef(location.pathname);
  const searchRef = useRef(location.search);
  const [isReady, setIsReady] = useState(false);
  // Whether we've already inserted (or confirmed existence of) the session row
  // in this tab. Reset only when the tab/sessionStorage is cleared.
  const sessionRowEnsured = useRef<boolean>(
    sessionStorage.getItem('analytics_session_inserted') === '1'
  );
  const sessionStartTime = useRef<number>((() => {
    const stored = sessionStorage.getItem('analytics_session_start');
    if (stored) return parseInt(stored, 10);
    const now = Date.now();
    sessionStorage.setItem('analytics_session_start', now.toString());
    return now;
  })());

  // Acquisition capture is synchronous (lt_acquisition is set in main.tsx),
  // so the initial session insert happens immediately on first paint instead
  // of waiting for requestIdleCallback (which often never fires on busy pages).
  // Engagement/heartbeat work below still happens after first render.
  useEffect(() => {
    setIsReady(true);
  }, []);

  useEffect(() => {
    pathnameRef.current = location.pathname;
    searchRef.current = location.search;
  }, [location.pathname, location.search]);

  // Backfill VB number once it appears in sessionStorage (BookingContext sets it async)
  const lastBackfilledVB = useRef<string | null>(null);
  useEffect(() => {
    if (!isReady) return;
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/auth')) return;
    if (isBot(navigator.userAgent)) return;

    const tryBackfill = async () => {
      const vb = extractVBNumber(location.pathname);
      if (!vb || vb === lastBackfilledVB.current) return;
      lastBackfilledVB.current = vb;
      try {
        await supabase.rpc('track_session', { p_session_id: sessionId.current, p_data: { vb_number: vb } });
      } catch (e) {
        console.error('[Analytics] VB backfill failed:', e);
      }
    };

    // Try immediately, then poll for 30s in case BookingContext is still fetching
    tryBackfill();
    const interval = setInterval(tryBackfill, 1000);
    const timeout = setTimeout(() => clearInterval(interval), 30000);

    // Event-driven: BookingContext dispatches this as soon as VB is in sessionStorage
    const handleVBSet = () => { tryBackfill(); };
    window.addEventListener('vb-number-set', handleVBSet);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      window.removeEventListener('vb-number-set', handleVBSet);
    };
  }, [isReady, location.pathname]);

  useEffect(() => {
    if (!isReady) return;

    // Skip only excluded routes; allow published domain and quote/booking flows.
    if (shouldExcludeFromAnalytics(location.pathname)) {
      return;
    }

    // Client-side bot detection (UA patterns + headless/automation fingerprint)
    const ua = navigator.userAgent;
    if (isBot(ua) || isLikelyHeadless()) {
      return;
    }

    const currentPath = location.pathname;
    const today = formatDate(new Date());

    // Increment pages viewed and add to history - persist to sessionStorage
    pagesViewed.current += 1;
    sessionStorage.setItem('analytics_pages_viewed', pagesViewed.current.toString());
    
    // Add every navigation to history to show the full journey (including revisits)
    // Also update visitor_page_history so both systems stay in sync
    pageHistory.current = [...pageHistory.current, currentPath].slice(-30);
    sessionStorage.setItem('analytics_page_history', JSON.stringify(pageHistory.current));
    sessionStorage.setItem('visitor_page_history', JSON.stringify(pageHistory.current));

    // Extract VB number and workflow step
    const vbNumber = extractVBNumber(currentPath);
    const workflowStep = getWorkflowStep(currentPath);

    const device = /Mobile|Android|iPhone/i.test(navigator.userAgent) 
      ? 'mobile' 
      : /Tablet|iPad/i.test(navigator.userAgent) 
        ? 'tablet' 
        : 'desktop';

    // Pull cached acquisition (set synchronously in main.tsx) so source/medium/
    // campaign/term reflect the ORIGINAL landing — not whatever document.referrer
    // happens to look like after SPA navigation.
    const acquisition = captureAcquisition();
    const source = acquisition.source;

    // Track pageview in page_analytics (aggregate table).
    // Use UPDATE-then-INSERT-on-zero-rows so existing rows actually increment
    // (previously the SELECT was denied by RLS, forcing an INSERT every time
    // and producing tons of pv:1 / uv:1 duplicate rows).
    const trackPageview = async () => {
      try {
        const { error } = await supabase.rpc('track_pageview', { p_path: currentPath, p_title: document.title });
        if (error) console.error('[Analytics] pageview error:', error);
      } catch (error) {
        console.error('[Analytics] Failed to track pageview:', error);
      }
    };

    // Track session — designed to work whether or not anon has SELECT on the
    // table. First page in this tab → plain INSERT (no upsert/RETURNING quirks).
    // If a duplicate-key error comes back the row already exists → switch to
    // UPDATE for the rest of the journey. Subsequent pages in the same tab →
    // UPDATE only (mutable journey fields), preserving original acquisition.
    const trackSession = async () => {
      const updatePayload: Record<string, unknown> = {
        pages_viewed: pagesViewed.current,
        exit_page: currentPath,
        page_history: pageHistory.current,
      };
      if (vbNumber) updatePayload.vb_number = vbNumber;
      if (workflowStep) updatePayload.workflow_step = workflowStep;

      try {
        if (sessionRowEnsured.current) {
          // Row already inserted (this tab) → just update journey fields.
          const { error: updErr } = await supabase.rpc('track_session', { p_session_id: sessionId.current, p_data: updatePayload as any });
          if (updErr) console.error('[Analytics] session update error:', updErr);
          return;
        }

        // First page of this tab → try INSERT.
        const { visitorId, isReturning } = ensureIdentifiers();
        const insertPayload = {
          session_id: sessionId.current,
          visitor_id: visitorId,
          is_returning: isReturning,
          entry_page: acquisition.entry_page || currentPath,
          source: acquisition.source,
          medium: acquisition.medium || (source === 'referral' ? acquisition.referrer_domain : null),
          campaign: acquisition.campaign,
          term: acquisition.term,
          device_type: device,
          browser: getBrowser(),
          pages_viewed: 1,
          page_history: [currentPath],
          vb_number: vbNumber,
          workflow_step: workflowStep,
        };

        // Human-signal gate: scripted hitters fire 1 pageview with 0s dwell
        // and no interaction. Wait for ANY of {mousemove/scroll/touch/keydown,
        // 2nd navigation, or 3s dwell} before creating the session row. If the
        // "visitor" leaves the tab before any signal fires, no row is written.
        await waitForHumanSignal();

        const { error: insErr } = await supabase.rpc('track_session', { p_session_id: sessionId.current, p_data: { ...insertPayload, create: true } as any });

        if (!insErr) {
          sessionRowEnsured.current = true;
          sessionStorage.setItem('analytics_session_inserted', '1');
          return;
        }

        // 23505 = unique_violation on session_id (e.g. survived from prior tab
        // refresh). Treat as ensured and move on with UPDATEs.
        if ((insErr as any).code === '23505') {
          sessionRowEnsured.current = true;
          sessionStorage.setItem('analytics_session_inserted', '1');
          await supabase.rpc('track_session', { p_session_id: sessionId.current, p_data: updatePayload as any });
          return;
        }

        console.error('[Analytics] session insert error:', insErr);
      } catch (error) {
        console.error('[Analytics] Failed to track session:', error);
      }
    };

    trackPageview();
    trackSession();

    // Track time on page when leaving — use sendBeacon with proper Supabase headers
    const handleBeforeUnload = () => {
      const duration = Math.round((Date.now() - sessionStartTime.current) / 1000);
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      
      const url = `${supabaseUrl}/rest/v1/rpc/track_session`;
      const body = JSON.stringify({
        p_session_id: sessionId.current,
        p_data: {
          duration_seconds: duration,
          exit_page: currentPath,
          pages_viewed: pagesViewed.current,
          page_history: pageHistory.current,
        },
      });
      
      // sendBeacon requires a Blob with Content-Type to set headers
      // But sendBeacon doesn't support custom headers — use fetch with keepalive instead
      try {
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
            'Prefer': 'return=minimal',
          },
          body,
          keepalive: true, // This is the key — ensures request survives page unload
        });
      } catch {
        // Fallback: try sendBeacon (will likely fail without headers, but better than nothing)
        navigator.sendBeacon?.(url, body);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // --- Engagement detection ---
    const markEngaged = async () => {
      if (sessionStorage.getItem('session_engaged')) return;
      sessionStorage.setItem('session_engaged', 'true');
      const { error } = await supabase.rpc('track_session', { p_session_id: sessionId.current, p_data: { engaged: true } });
      if (error) console.error('[Analytics] Failed to mark engaged:', error);
    };

    // Heartbeat: update duration every 30 seconds so we never lose tracking
    const heartbeatInterval = setInterval(async () => {
      const duration = Math.round((Date.now() - sessionStartTime.current) / 1000);
      
      // 30s+ on page = engaged
      if (!sessionStorage.getItem('session_engaged')) {
        markEngaged();
      }
      
      const { error } = await supabase.rpc('track_session', {
        p_session_id: sessionId.current,
        p_data: {
          duration_seconds: duration,
          pages_viewed: pagesViewed.current,
          page_history: pageHistory.current,
          exit_page: currentPath,
        },
      });
      if (error) console.error('[Analytics] Heartbeat update failed:', error);
    }, 30000);

    // Scroll ≥ 25% = engaged
    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (scrollHeight > 0 && scrollTop / scrollHeight >= 0.25) {
        markEngaged();
        window.removeEventListener('scroll', handleScroll);
      }
    };

    // Any click/tap = engaged
    const handleClick = () => {
      markEngaged();
      window.removeEventListener('click', handleClick);
    };

    if (!sessionStorage.getItem('session_engaged')) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('click', handleClick, { passive: true });
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      clearInterval(heartbeatInterval);
    };
  }, [location.pathname, location.search, isReady]);
}

export default usePageAnalytics;
